/* UserBox.js */
/* @author: anghello */

import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  DropdownToggle,
  DropdownMenu,
  Nav,
  Button,
  NavItem,
  NavLink,
  UncontrolledButtonDropdown,
} from 'reactstrap';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useAuth } from '../../../Context/Auth/AuthContext';
import background from '../../../assets/utils/images/dropdown-header/abstract10.jpg';
import Strings from '../../../translations/Translations';
import SubscriptionsStatuses from '../../../enums/SubscriptionsStatuses';
import { Subscriptions } from '../../../enums/Subscriptions';
import ModalCredits from '../../../modules/UserCredits/ModalCredits';

export default function UserBox() {
  const history = useHistory();
  const {
    logout,
    authenticated,
    toggleLoading,
    globalCreditsUserBalance,
    globalCreditsUserData,
    getUserBalance,
    getUserDataCredits,
    setOpenModalSubscribe
  } = useAuth();
  const menuHandler = useRef();

  const [avatarSrc, setAvatarSrc] = useState(process.env.REACT_APP_PZ_INPUT_USER_AVATAR_SRC);
  const [username, setUsername] = useState('');

  useEffect(() => {
    if (authenticated && authenticated.un) {
      setAvatarSrc(avatarSrc.replace('{username}', authenticated.un));
      setUsername(authenticated.un);
    }
  }, [authenticated, avatarSrc, setAvatarSrc, setUsername]);

  const handleLogout = useCallback(async () => {
    toggleLoading(true);
    await logout();
    toggleLoading(false);
    history.replace('/');
  }, [logout, history, toggleLoading]);

  const goToAccount = useCallback(async () => {
    history.replace('/account');
    menuHandler.current.toggle();
  }, [history]);

  const goToExportedVideos = useCallback(async () => {
    history.replace('/exported-videos');
    menuHandler.current.toggle();
  }, [history]);

  useEffect(() => {
    getUserBalance();
    getUserDataCredits();
  }, [getUserBalance, getUserDataCredits]);

  const getMyPlanLabel = useCallback(() => {
    if (globalCreditsUserData && globalCreditsUserData.subscriptions && globalCreditsUserData.subscriptions[0]) {
      const status = parseInt(globalCreditsUserData.subscriptions[0].status);
      const type = parseInt(globalCreditsUserData.subscriptions[0].type);

      if (status !== SubscriptionsStatuses.STATUS.CANCELED && status !== SubscriptionsStatuses.STATUS.EXPIRED && status !== SubscriptionsStatuses.STATUS.PENDING) {
        if (type === SubscriptionsStatuses.TYPE.FREE) {
          return Strings('free');
        } else if (type === SubscriptionsStatuses.TYPE.PLUS) {
          return Subscriptions.plus.title;
        } else if (type === SubscriptionsStatuses.TYPE.PREMIUM) {
          return Subscriptions.premium.title;
        } else if (type === SubscriptionsStatuses.TYPE.MAX) {
          return Subscriptions.max.title;
        }
      } else {
        return Strings('free');
      }
    }
  }, [globalCreditsUserData]);

  const linkGetCredits = useCallback((evt) => {
    evt.preventDefault();
    menuHandler.current.toggle();
    setOpenModalSubscribe(true);
  }, [setOpenModalSubscribe]);

  return (
    <Fragment>
      <div className='header-btn-lg pr-0'>
        <div className='widget-content p-0'>
          <div className='widget-content-wrapper'>

            <div className='widget-content-left'>
              <UncontrolledButtonDropdown ref={menuHandler} >
                <DropdownToggle
                  color='link'
                  className='p-0'
                >
                  <img
                    width={42}
                    className='rounded-circle'
                    src={avatarSrc}
                    alt={username}
                  />
                  <FontAwesomeIcon
                    className='ml-2 opacity-8'
                    icon={faAngleDown}
                  />
                </DropdownToggle>
                <DropdownMenu
                  right
                  className='rm-pointers dropdown-menu-lg dropdown-header-box'
                >
                  <div className='dropdown-menu-header'>
                    <div className='dropdown-menu-header-inner bg-info'>
                      <div
                        className='menu-header-image opacity-2'
                        style={{ backgroundImage: 'url(' + background + ')' }}
                      />
                      <div className='menu-header-content text-left'>
                        <div className='widget-content p-0'>
                          <div className='widget-content-wrapper'>
                            <div className='widget-content-left mr-3'>
                              <img
                                width={42}
                                className='rounded-circle'
                                src={avatarSrc}
                                alt={username}
                              />
                            </div>
                            <div className='widget-content-left'>
                              <div className='widget-heading'>
                                {authenticated.un}
                              </div>
                            </div>
                            <div className='widget-content-right mr-2'>
                              <Button
                                className='btn-pill btn-shadow btn-shine'
                                color='focus'
                                onClick={
                                  handleLogout
                                }
                              >
                                {Strings('logout')}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='scroll-area-xs' style={{ height: '250px' }}>
                    <PerfectScrollbar>
                      <Nav vertical>
                        <NavItem className='nav-item-header'>
                          {Strings('my_plan')}
                        </NavItem>

                        <NavItem>
                          <div className='nav-link d-flex justify-content-between'>
                            <span className='subscription' style={{fontSize: '18px'}}>
                              {getMyPlanLabel()}
                            </span>
                            <span className='subtitle link'>
                              <a href='#/account' onClick={() => { menuHandler.current.toggle(); }}>{Strings('manage')}</a>
                            </span>
                          </div>
                        </NavItem>

                        <NavItem className='nav-item-header'>
                          {Strings('credits')}
                        </NavItem>

                        <NavItem>
                          <div className='nav-link d-flex justify-content-between'>
                            <div>
                              <span className='credits' style={{fontSize: '18px'}}>
                                {globalCreditsUserBalance && globalCreditsUserBalance.credits_total}
                              </span>
                            </div>
                            <span className='subtitle link'>
                              <a href='#/account' onClick={linkGetCredits}>{Strings('get_credits')}</a>
                            </span>
                          </div>
                        </NavItem>

                        <NavItem className='nav-item-header'>
                          {Strings('account')}
                        </NavItem>

                        <NavItem>
                          <NavLink onClick={goToAccount}>
                            {Strings('my_account')}
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink onClick={goToExportedVideos}>
                            {Strings('exported_videos')}
                          </NavLink>
                        </NavItem>
                      </Nav>
                    </PerfectScrollbar>
                  </div>
                </DropdownMenu>
              </UncontrolledButtonDropdown>
            </div>
          </div>
        </div>
      </div>

      <ModalCredits />
    </Fragment>
  );
}
